import type { ReactNode } from "react";
import type { Icon as IconType } from "../Icon";
import { Icon } from "../Icon";

type IconName = React.ComponentProps<typeof IconType>["name"];

export type TimelineItemVariant = "default" | "active" | "muted" | "success" | "error" | "warning" | "info";

export interface TimelineItemProps {
  children: ReactNode;
  /** Visual variant of the timeline item */
  variant?: TimelineItemVariant;
  /** Optional icon to display in the connector dot */
  icon?: IconName;
  /** Label text displayed above the main content */
  label?: string;
  /** Whether this is the last item (hides the connecting line below) */
  isLast?: boolean;
  className?: string;
}

/**
 * TimelineItem
 *
 * Individual item in a Timeline component. Displays content with a connecting line
 * and optional icon/label. Supports different visual variants for emphasis.
 *
 * @example
 * ```tsx
 * <Timeline>
 *   <TimelineItem label="Site" icon="MapPin">1020 Jordan Lane</TimelineItem>
 *   <TimelineItem label="Utility" icon="Lightning">Pacific Gas & Electric</TimelineItem>
 *   <TimelineItem label="ISO Market" icon="ChartNetwork" isLast>CAISO</TimelineItem>
 * </Timeline>
 * ```
 */
export function TimelineItem({
  children,
  variant = "default",
  icon,
  label,
  isLast = false,
  className = "",
}: TimelineItemProps) {
  // Variant styles
  const variantStyles = {
    default: {
      dot: "bg-border-default border-border-default",
      line: "bg-border-default",
      label: "text-text-secondary",
      content: "text-text-primary",
    },
    active: {
      dot: "bg-text-primary border-text-primary",
      line: "bg-border-default",
      label: "text-text-secondary",
      content: "text-text-primary font-medium",
    },
    muted: {
      dot: "bg-border-muted border-border-muted",
      line: "bg-border-muted",
      label: "text-text-secondary",
      content: "text-text-secondary",
    },
    success: {
      dot: "bg-feedback-success-border border-feedback-success-border",
      line: "bg-border-default",
      label: "text-text-secondary",
      content: "text-text-primary",
    },
    error: {
      dot: "bg-feedback-error-border border-feedback-error-border",
      line: "bg-border-default",
      label: "text-text-secondary",
      content: "text-text-primary",
    },
    warning: {
      dot: "bg-feedback-warning-border border-feedback-warning-border",
      line: "bg-border-default",
      label: "text-text-secondary",
      content: "text-text-primary",
    },
    info: {
      dot: "bg-feedback-info-border border-feedback-info-border",
      line: "bg-border-default",
      label: "text-text-secondary",
      content: "text-text-primary",
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`flex gap-3 ${className}`.trim()} role="listitem">
      {/* Connector column */}
      <div className="flex flex-col items-center" style={{ width: "20px" }}>
        {/* Dot/Icon */}
        <div
          className={`flex items-center justify-center rounded-full border-2 ${styles.dot}`}
          style={{ width: "20px", height: "20px", flexShrink: 0 }}
        >
          {icon && (
            <Icon
              name={icon}
              size={12}
              className={variant === "active" ? "text-background-body" : "text-background-body"}
            />
          )}
        </div>

        {/* Connecting line */}
        {!isLast && <div className={`w-0.5 flex-1 ${styles.line}`} style={{ minHeight: "24px" }} />}
      </div>

      {/* Content column */}
      <div className="flex-1 pb-6">
        {label && <div className={`text-xs mb-1 ${styles.label}`}>{label}</div>}
        <div className={`text-sm ${styles.content}`}>{children}</div>
      </div>
    </div>
  );
}
